/*!
 * Platform Context - 管理當前平台狀態
 * 
 * 平台：
 * - OceanAtlas (oa)
 * - TinyPoker (tiny)
 * - 跨平台 (common) - Revenue 等
 */
import React, { createContext, useState, useContext, useEffect } from "react";

// Logo imports
import oaLogo from "assets/img/OceanAtlas_logo.png";
import tinyLogo from "assets/img/Tiny-Poker_logo.png";

// 平台定義
export const PLATFORMS = {
  OA: "oa",
  TINY: "tiny",
  COMMON: "common", // 跨平台
};

// 平台資訊
export const PLATFORM_INFO = {
  [PLATFORMS.OA]: {
    name: "OceanAtlas",
    shortName: "OA",
    icon: "fa fa-ship",
    color: "#1d8cf8", // 藍色
    description: "OceanAtlas 聯盟管理",
    logo: oaLogo,
  },
  [PLATFORMS.TINY]: {
    name: "TinyPoker",
    shortName: "TiNY", // 只有 i 小寫（Brad 2026-09-18）
    icon: "fa fa-diamond",
    color: "#c9772b", // 琥珀（取自木頭 logo；原本的深咖啡 #5b3a29 在藍色側欄上看不出來，v2.39.0 改）
    description: "TinyPoker 聯盟管理",
    logo: tinyLogo,
  },
};

// Context
const PlatformContext = createContext();

// Provider
export function PlatformProvider({ children }) {
  // 從 localStorage 讀取，預設為 TinyPoker（因為這是主要開發目標）
  const [currentPlatform, setCurrentPlatform] = useState(() => {
    const saved = localStorage.getItem("currentPlatform");
    return saved && Object.values(PLATFORMS).includes(saved)
      ? saved
      : PLATFORMS.TINY;
  });

  // 保存到 localStorage
  useEffect(() => {
    localStorage.setItem("currentPlatform", currentPlatform);
  }, [currentPlatform]);

  // 切換平台
  const switchPlatform = (platform) => {
    if (Object.values(PLATFORMS).includes(platform)) {
      setCurrentPlatform(platform);
    }
  };

  // 取得當前平台資訊
  const getPlatformInfo = () => PLATFORM_INFO[currentPlatform];

  // 判斷是否為當前平台
  const isPlatform = (platform) => currentPlatform === platform;

  const value = {
    currentPlatform,
    setCurrentPlatform,
    switchPlatform,
    getPlatformInfo,
    isPlatform,
    PLATFORMS,
    PLATFORM_INFO,
  };

  return (
    <PlatformContext.Provider value={value}>
      {children}
    </PlatformContext.Provider>
  );
}

// Hook
export function usePlatform() {
  const context = useContext(PlatformContext);
  if (!context) {
    throw new Error("usePlatform must be used within a PlatformProvider");
  }
  return context;
}

export default PlatformContext;

